import React, { useState, useEffect, useRef } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';

const LyricAIPopup = ({ onInsert, label = 'Ask Lyric', style = {}, selectedModel, currentValue = '', getSelectedText }) => {
  // Helper function for authenticated API calls
  const authenticatedFetch = async (url, options = {}) => {
    const token = await authService.getAccessToken();
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    };
    return fetch(url, { ...options, headers });
  };

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [modelUsed, setModelUsed] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [selectedAnswerText, setSelectedAnswerText] = useState(''); // Track selected text from answer
  const popupRef = useRef(null);
  const answerRef = useRef(null);

  // Maintain textarea selection visibility when popup is open
  useEffect(() => {
    if (open && selectionRange) {
      const textarea = document.getElementById('issue-description');
      if (textarea) {
        // Keep textarea focused and selection visible
        textarea.focus();
        textarea.setSelectionRange(selectionRange.start, selectionRange.end);
        
        // Prevent losing selection by keeping focus
        const maintainFocus = () => {
          if (document.activeElement !== textarea) {
            textarea.focus();
            textarea.setSelectionRange(selectionRange.start, selectionRange.end);
          }
        };
        
        // Check periodically
        const interval = setInterval(maintainFocus, 50);
        
        return () => clearInterval(interval);
      }
    }
  }, [open, selectionRange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the popup
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        // Don't close if clicking on the description textarea
        const descriptionTextarea = document.getElementById('issue-description');
        if (descriptionTextarea && (descriptionTextarea === event.target || descriptionTextarea.contains(event.target))) {
          return;
        }
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Clear results when selection changes
  useEffect(() => {
    if (open) {
      setAnswer('');
      setError(null);
    }
  }, [selectedText, open]);

  // Detect when user selects text from the answer
  const handleAnswerSelection = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    setSelectedAnswerText(selectedText);
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setAnswer('');
    setSelectedAnswerText('');
    try {
      // Construct the absolute URL to ensure the request hits the backend on port 3001
      const apiUrl = getApiBase();
      const fullUrl = `${apiUrl}/api/ai/ask`;

      // Include selected text as context if available, otherwise include current field value
      let promptWithContext = question;
      const hasSelectedText = selectedText && selectedText.trim();
      const hasCurrentValue = currentValue && currentValue.trim();
      
      if (hasSelectedText) {
        // When text is selected, ask for improvement options
        promptWithContext = `Selected text to improve: "${selectedText}"\n\nUser instruction: ${question}`;
      } else if (hasCurrentValue) {
        // When no text is selected but we have a current value (e.g., Summary field), include it as context
        promptWithContext = `Current ${label || 'field'} content: "${currentValue}"\n\nUser question: ${question}`;
      } else {
        // When no text is selected and no current value, just answer the question directly
        promptWithContext = question;
      }

      // The backend expects 'message', not 'prompt'.
      const response = await authenticatedFetch(fullUrl, {
        method: 'POST',
        body: JSON.stringify({ 
          message: promptWithContext, 
          selectedModel,
          isTextImprovement: hasSelectedText // Tell backend if this is text improvement
        }),
      });

      if (!response.ok) {
        // Get error message from the server's JSON response
        const errorData = await response.json();
        throw new Error(errorData.error || `API Error: ${response.status}`);
      }

      // The backend returns { response, modelUsed }
      const data = await response.json();
      setAnswer(data.response || data.text);
      setModelUsed(data.modelUsed || selectedModel);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!currentValue.trim()) {
      setError('No content to review. Please enter text in the field first.');
      return;
    }
    
    setReviewing(true);
    setLoading(true);
    setError(null);
    setAnswer('');
    
    try {
      const apiUrl = getApiBase();
      const fullUrl = `${apiUrl}/api/ai/ask`;

      const reviewPrompt = `Please review the following text and provide suggestions for improvement or give a status assessment. Include feedback on clarity, completeness, and any recommendations:\n\n"${currentValue}"`;

      const response = await authenticatedFetch(fullUrl, {
        method: 'POST',
        body: JSON.stringify({ message: reviewPrompt, selectedModel }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API Error: ${response.status}`);
      }

      const data = await response.json();
      setAnswer(data.response || data.text);
      setModelUsed(data.modelUsed || selectedModel);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setReviewing(false);
    }
  };

  return (
    <span style={{ position: 'relative', ...style }} ref={popupRef}>
      <button 
        type="button" 
        onMouseDown={(e) => {
          // Prevent button from stealing focus
          e.preventDefault();
        }}
        onClick={(e) => {
          if (!open && getSelectedText) {
            const selected = getSelectedText();
            setSelectedText(selected);
            
            // Store the textarea selection range BEFORE opening popup
            const textarea = document.getElementById('issue-description');
            if (textarea && selected && selected.trim()) {
              const range = {
                start: textarea.selectionStart,
                end: textarea.selectionEnd
              };
              setSelectionRange(range);
              setQuestion('Improve this text');
              
              // Open popup then immediately refocus textarea
              setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(range.start, range.end);
              }, 0);
            } else {
              setSelectionRange(null);
              setQuestion('');
              setAnswer('');
              setError(null);
              setSelectedAnswerText('');
            }
          }
          setOpen(!open);
        }} 
        style={{ 
          width: '30px',
          height: '30px',
          padding: '0',
          marginLeft: style.marginLeft !== undefined ? style.marginLeft : 8, 
          cursor: 'pointer',
          border: 'none',
          borderRadius: '4px',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1rem'
        }}
      >
        💬
      </button>
      {open && (
        <div style={{
          position: 'fixed', 
          zIndex: 1000, 
          top: '12vh', 
          left: 'calc(50% - 250px)',
          background: '#fff', 
          border: '1px solid #ccc', 
          borderRadius: 8, 
          padding: 16, 
          width: '500px',
          height: '60vh',
          minWidth: '320px',
          minHeight: '240px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          resize: 'both',
          overflow: 'auto',
          color: '#222',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>{label}</div>
          {selectedText && selectedText.trim() && (
            <div style={{ 
              marginBottom: 8, 
              padding: '8px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '0.85rem',
              color: '#856404'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>✏️ Selected text to improve:</strong>
                <button
                  onClick={() => {
                    setSelectedText('');
                    setQuestion('');
                  }}
                  style={{
                    padding: '2px 6px',
                    fontSize: '0.75rem',
                    backgroundColor: 'transparent',
                    border: '1px solid #856404',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    color: '#856404'
                  }}
                >
                  Clear
                </button>
              </div>
              <div style={{ 
                marginTop: '4px', 
                fontStyle: 'italic',
                maxHeight: '60px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                "{selectedText.substring(0, 150)}{selectedText.length > 150 ? '...' : ''}"
              </div>
            </div>
          )}
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder={selectedText && selectedText.trim() 
              ? "How should I improve the selected text? (e.g., 'Make it more concise', 'Add more detail', 'Improve clarity')"
              : "Ask a question for Lyric... (e.g., 'Is this a good name?', 'How can I improve this?')"
            }
            rows={2}
            style={{ 
              width: '100%', 
              marginBottom: 8, 
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              color: '#222',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <button onClick={() => handleAsk()} disabled={loading || !question.trim()} style={{ marginRight: 8 }}>
                {loading && !reviewing 
                  ? (selectedText && selectedText.trim() ? 'Processing...' : 'Asking...') 
                  : (selectedText && selectedText.trim() ? 'Go' : 'Ask')
                }
              </button>
              <button onClick={() => setOpen(false)} style={{ marginRight: 8 }}>Close</button>
            </div>
            {/* Review button hidden for now */}
            {false && (
              <button 
                onClick={handleReview} 
                disabled={loading || !currentValue.trim()}
                style={{ 
                  padding: '6px 12px',
                  backgroundColor: '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || !currentValue.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  opacity: loading || !currentValue.trim() ? 0.6 : 1
                }}
              >
                {reviewing ? 'Reviewing...' : 'Review'}
              </button>
            )}
          </div>
          {answer && (
            <div style={{ marginTop: 12 }}>
              {/* Show different header based on whether text was selected */}
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                {selectedText && selectedText.trim() ? 'Select an option' : 'Answer'} {modelUsed && <span style={{ color: '#2196f3', fontWeight: 'normal' }}>({modelUsed})</span>}:
              </div>
              <div style={{ 
                background: '#f8f9fa', 
                padding: 12, 
                borderRadius: 4, 
                marginBottom: 12, 
                maxHeight: '300px', 
                overflowY: 'auto'
              }}>
                {(() => {
                  // If no text was selected, just show the answer as-is (it's a question response)
                  const hasSelectedText = selectedText && selectedText.trim();
                  
                  if (!hasSelectedText) {
                    // Just display the answer directly as formatted text (don't return, continue to button below)
                    return (
                      <>
                        <div 
                          ref={answerRef}
                          onMouseUp={handleAnswerSelection}
                          style={{
                            padding: '8px',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.95rem',
                            color: '#333',
                            userSelect: 'text',
                            cursor: 'text'
                          }}>
                          {answer}
                        </div>
                        {selectedAnswerText && (
                          <div style={{
                            marginTop: '8px',
                            padding: '6px 8px',
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #2196f3',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            color: '#1976d2'
                          }}>
                            ✓ Selected: {selectedAnswerText.length > 50 ? selectedAnswerText.substring(0, 50) + '...' : selectedAnswerText}
                          </div>
                        )}
                      </>
                    );
                  }
                  
                  // Simple parsing - split by lines and clean aggressively (for text improvement mode)
                  let options = answer
                    .split('\n')
                    .map(line => {
                      let cleaned = line.trim();
                      
                      // Remove any numbering (1., 2), 3:, etc)
                      cleaned = cleaned.replace(/^\d+[.):-]\s*/g, '');
                      
                      // Remove bullets (-, *, •)
                      cleaned = cleaned.replace(/^[-*•]\s*/g, '');
                      
                      // Remove markdown bold
                      cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, '$1');
                      
                      // Remove "Option X:" style prefixes
                      cleaned = cleaned.replace(/^option\s+\d+:?\s*/gi, '');
                      
                      // Remove any prefix with colon (like "Better:", "Improvement:", etc)
                      cleaned = cleaned.replace(/^[^:]+:\s*/g, '');
                      
                      // Remove content in parentheses at the end
                      cleaned = cleaned.replace(/\s*\([^)]*\)\s*$/g, '');
                      
                      // Remove content after " - " (explanations)
                      cleaned = cleaned.replace(/\s+-\s+.*$/g, '');
                      
                      // Remove all types of quotes at start/end
                      cleaned = cleaned.replace(/^["'"'`´]+|["'"'`´]+$/g, '');
                      
                      return cleaned.trim();
                    })
                    .filter(line => {
                      // Keep only lines that look like actual text options
                      if (!line || line.length < 5) return false;
                      
                      const lower = line.toLowerCase();
                      
                      // Reject explanatory text
                      if (lower.startsWith('here are')) return false;
                      if (lower.startsWith('here is')) return false;
                      if (lower.startsWith('note:')) return false;
                      if (lower.startsWith('for ')) return false;
                      if (lower.startsWith('removing ')) return false;
                      if (lower.startsWith('stronger ')) return false;
                      if (lower.startsWith('focusing ')) return false;
                      if (lower.startsWith('emphasizing')) return false;
                      if (lower.startsWith('because')) return false;
                      if (lower.startsWith('this ')) return false;
                      if (lower.startsWith('which ')) return false;
                      if (lower.includes('often preferred')) return false;
                      if (lower.includes('are stronger')) return false;
                      if (lower.includes('more appropriate')) return false;
                      if (lower.includes('better readability')) return false;
                      if (lower.includes('agile')) return false;
                      if (lower.includes('marketing')) return false;
                      if (lower.includes('technical documentation')) return false;
                      
                      return true;
                    });
                  
                  // Show clickable options for both selected and non-selected text
                  
                  return options.map((option, index) => {
                    // Always use clickable text blocks (no radio buttons)
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          onInsert(option);
                          setOpen(false);
                        }}
                        style={{
                          display: 'block',
                          padding: '12px',
                          marginBottom: '8px',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontSize: '0.95rem',
                          lineHeight: '1.5',
                          userSelect: 'text'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e3f2fd';
                          e.currentTarget.style.borderColor = '#2196f3';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#ddd';
                        }}
                      >
                        {option}
                      </div>
                    );
                  });
                })()}
              </div>
              {/* Show appropriate button based on mode */}
              {(() => {
                const hasSelectedText = selectedText && selectedText.trim();
                
                if (hasSelectedText) {
                  // Selected text mode - options are clickable, no separate button needed
                  return null;
                } else {
                  // No selected text - show insert button for answer (full or selected portion)
                  const textToInsert = selectedAnswerText || answer;
                  const buttonText = selectedAnswerText 
                    ? '✓ Insert selected text' 
                    : '✓ Insert answer';
                  
                  return (
                    <button 
                      onClick={() => {
                        onInsert(textToInsert);
                        setOpen(false);
                        setSelectedAnswerText(''); // Clear selection
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        marginTop: '8px'
                      }}
                    >
                      {buttonText}
                    </button>
                  );
                }
              })()}
            </div>
          )}
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </div>
      )}
    </span>
  );
};

export default LyricAIPopup;